import type { UpsertServiceRequest } from "@planerius/contracts/cabinet"

import type { ServiceView } from "../../../api/directory.api"
import { minutesLabel } from "../../../lib/plural"

/**
 * КАРТОЧКА УСЛУГИ (3-Б2) И ШАГИ СОСТАВНОЙ (3-Б3) — правила без вёрстки.
 *
 * Сохранение переписывает услугу ЦЕЛИКОМ, поэтому форма собирается из всего, что отдал
 * каталог, включая требования к ресурсам и шаги: поле, которого нет в форме, при сохранении
 * обнулилось бы. Ровно из-за этого кнопка «Сохранить» стояла выключенной, пока каталог
 * не отдавал «Кресло — 1».
 */

export type ServiceKind = "simple" | "composite"

export interface StepDraft {
  /** Ключ строки в списке — не номер: номер меняется при перестановке. */
  readonly key: string
  readonly name: string
  readonly minutes: string
  readonly occupiesEmployee: boolean
  readonly occupiesResource: boolean
}

export interface ServiceForm {
  readonly name: string
  readonly kind: ServiceKind
  readonly categoryId: string | null
  readonly minutes: string
  readonly bufferBefore: string
  readonly bufferAfter: string
  readonly price: string
  readonly isOnlineBookable: boolean
  readonly isActive: boolean
  readonly resources: readonly { readonly resourceTypeId: string; readonly qty: number }[]
  readonly steps: readonly StepDraft[]
}

let keySeq = 0
export function stepKey(): string {
  keySeq += 1
  return `step-${keySeq}`
}

export function formOf(service: ServiceView): ServiceForm {
  return {
    name: service.name,
    kind: service.kind === "composite" ? "composite" : "simple",
    categoryId: service.categoryId,
    minutes: String(service.durationMinutes),
    bufferBefore: String(service.bufferBeforeMinutes),
    bufferAfter: String(service.bufferAfterMinutes),
    price: rublesOf(service.priceMinor),
    isOnlineBookable: service.isOnlineBookable,
    isActive: service.isActive,
    resources: service.resourceRequirements.map((item) => ({ resourceTypeId: item.resourceTypeId, qty: item.qty })),
    steps: service.steps
      .toSorted((a, b) => a.ord - b.ord)
      .map((step) => ({
        key: stepKey(),
        name: step.name ?? "",
        minutes: String(step.durationMinutes),
        occupiesEmployee: step.occupiesEmployee,
        occupiesResource: step.occupiesResource,
      })),
  }
}

export function emptyForm(categoryId: string | null): ServiceForm {
  return {
    name: "",
    kind: "simple",
    categoryId,
    minutes: "60",
    bufferBefore: "0",
    bufferAfter: "0",
    price: "",
    isOnlineBookable: true,
    isActive: true,
    resources: [],
    steps: [],
  }
}

/**
 * Готовый набор окрашивания (ТЗ Б3, «Пусто»): отправная точка и заодно пример того,
 * как это принято заполнять — пауза в середине, кресло занято всё время.
 */
export function coloringPreset(): StepDraft[] {
  return [
    { key: stepKey(), name: "Нанесение", minutes: "30", occupiesEmployee: true, occupiesResource: true },
    { key: stepKey(), name: "Выдержка", minutes: "40", occupiesEmployee: false, occupiesResource: true },
    { key: stepKey(), name: "Смывка и укладка", minutes: "30", occupiesEmployee: true, occupiesResource: true },
  ]
}

export function blankStep(): StepDraft {
  return { key: stepKey(), name: "", minutes: "", occupiesEmployee: true, occupiesResource: true }
}

export function minutesOf(value: string): number | null {
  const digits = value.replace(/[^\d]/g, "")
  if (digits === "") return null
  return Number(digits)
}

export function totalMinutes(steps: readonly StepDraft[]): number {
  return steps.reduce((sum, step) => sum + (minutesOf(step.minutes) ?? 0), 0)
}

/** Сколько минут мастер свободен внутри услуги — «окно» для другого клиента. */
export function pauseMinutes(steps: readonly StepDraft[]): number {
  return steps.filter((step) => !step.occupiesEmployee).reduce((sum, step) => sum + (minutesOf(step.minutes) ?? 0), 0)
}

/**
 * Что не так с шагами — теми же словами, что в ТЗ. null — сохранять можно.
 * Сервер проверяет то же самое; здесь — чтобы человек увидел причину до нажатия.
 */
export function stepsProblem(steps: readonly StepDraft[]): string | null {
  if (steps.length < 2) return "У составной услуги не меньше двух шагов. Если шаг один — это обычная услуга."
  for (const step of steps) {
    if (step.name.trim() === "") return "У каждого шага должно быть название."
    const minutes = minutesOf(step.minutes)
    if (minutes === null || minutes === 0) return `У шага «${step.name.trim()}» должна быть длительность.`
    if (minutes < 5) return `Шаг «${step.name.trim()}» короче 5 минут — так мелко журнал не размечает.`
    if (!step.occupiesEmployee && !step.occupiesResource) {
      return `На шаге «${step.name.trim()}» не занят ни мастер, ни кресло. Что тогда происходит эти ${minutesLabel(minutes)}? Отметьте хотя бы одно.`
    }
  }
  if (!steps[0]?.occupiesEmployee || !steps.at(-1)?.occupiesEmployee) {
    return "Первый и последний шаг занимают мастера: услуга не может начинаться или заканчиваться паузой."
  }
  if (totalMinutes(steps) > 24 * 60) return "Шаги вместе длиннее суток."
  return null
}

/** Все шаги заняты мастером — законно, но стоит спросить (ТЗ Б3). */
export function noPauseQuestion(steps: readonly StepDraft[]): string | null {
  if (steps.length < 2 || steps.some((step) => !step.occupiesEmployee)) return null
  return "Ни на одном шаге мастер не свободен. Тогда это обычная услуга — сделать её простой?"
}

export function moveStep(steps: readonly StepDraft[], index: number, delta: -1 | 1): StepDraft[] {
  const next = [...steps]
  const target = index + delta
  if (target < 0 || target >= next.length) return next
  const [item] = next.splice(index, 1)
  if (item !== undefined) next.splice(target, 0, item)
  return next
}

export interface TimelineCell {
  readonly key: string
  readonly name: string
  readonly minutes: number
  readonly percent: number
  readonly employee: boolean
  readonly resource: boolean
}

/** Полоса времени: две дорожки — «мастер» и «кресло» — по шагам, в долях от общей длины. */
export function timelineOf(steps: readonly StepDraft[]): { readonly cells: TimelineCell[]; readonly ticks: { readonly left: number; readonly label: string }[] } {
  const total = totalMinutes(steps)
  if (total === 0) return { cells: [], ticks: [] }
  let acc = 0
  const ticks = [{ left: 0, label: "0" }]
  const cells = steps.map((step) => {
    const minutes = minutesOf(step.minutes) ?? 0
    acc += minutes
    ticks.push({ left: (acc / total) * 100, label: `${acc} мин` })
    return {
      key: step.key,
      name: step.name.trim() === "" ? "шаг" : step.name.trim(),
      minutes,
      percent: (minutes / total) * 100,
      employee: step.occupiesEmployee,
      resource: step.occupiesResource,
    }
  })
  return { cells, ticks }
}

export function rublesOf(minor: number): string {
  return minor === 0 ? "" : String(minor / 100).replace(".", ",")
}

/** «1 800,00 ₽», «1800», «1800.5» → копейки; пусто — 0; мусор — null. */
export function minorOf(value: string): number | null {
  const cleaned = value.replace(/[\s ₽]/g, "").replace(",", ".")
  if (cleaned === "") return 0
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100)
}

/** Что не так с карточкой целиком; null — сохранять можно. */
export function formProblem(form: ServiceForm): string | null {
  if (form.name.trim() === "") return "Назовите услугу — так её увидит клиент."
  if (minorOf(form.price) === null) return "Цена — числом в рублях: 1800 или 1800,50."
  for (const [label, value] of [
    ["Буфер до", form.bufferBefore],
    ["Буфер после", form.bufferAfter],
  ] as const) {
    const minutes = minutesOf(value) ?? 0
    if (minutes > 240) return `${label} — не больше 240 минут.`
  }
  if (form.kind === "composite") return stepsProblem(form.steps)
  const minutes = minutesOf(form.minutes)
  if (minutes === null || minutes < 5) return "Длительность — не меньше 5 минут."
  if (minutes > 24 * 60) return "Длительность — не больше суток."
  return null
}

/** Запрос сохранения: всё, что есть у услуги, — иначе сохранение обнулит недостающее. */
export function requestOf(
  form: ServiceForm,
  current: ServiceView | null,
): UpsertServiceRequest {
  const composite = form.kind === "composite"
  return {
    ...(current === null ? {} : { id: current.id }),
    ...(current?.updatedAt === null || current === null ? {} : { expectedUpdatedAt: current.updatedAt }),
    branchId: current?.branchId ?? null,
    categoryId: form.categoryId,
    kind: form.kind,
    name: form.name.trim(),
    description: current?.description ?? null,
    durationMinutes: composite ? totalMinutes(form.steps) : (minutesOf(form.minutes) ?? 0),
    bufferBeforeMinutes: minutesOf(form.bufferBefore) ?? 0,
    bufferAfterMinutes: minutesOf(form.bufferAfter) ?? 0,
    capacity: current?.capacity ?? 1,
    priceMinor: minorOf(form.price) ?? 0,
    isOnlineBookable: form.isOnlineBookable,
    isActive: form.isActive,
    ord: current?.ord ?? 0,
    steps: composite
      ? form.steps.map((step, index) => ({
          ord: index + 1,
          name: step.name.trim(),
          durationMinutes: minutesOf(step.minutes) ?? 0,
          occupiesEmployee: step.occupiesEmployee,
          occupiesResource: step.occupiesResource,
        }))
      : [],
    resourceRequirements: [...form.resources],
  }
}

/** «У 4 клиентов, записанных на окрашивание, расписание останется прежним». */
export function futureNote(count: number, name: string): string {
  if (count === 0) return "Изменения подействуют на новые записи."
  const mod10 = count % 10
  const mod100 = count % 100
  const clients =
    mod10 === 1 && mod100 !== 11
      ? "клиента"
      : "клиентов"
  return `Изменения подействуют на новые записи — у ${count} ${clients}, записанных на «${name}», расписание останется прежним.`
}
